import { useTranslation } from 'react-i18next'

import { EyeReview } from '@ps/ds/icons/EyeReview'
import { Check, type LucideIcon, X } from 'lucide-react'
import { NS } from 'src/constants/i18n'
import { TreatmentStatus } from 'src/features/treatment/constants/treatment'

type TreatmentStatusConfig = {
  icon: LucideIcon
  backgroundColor: string
  iconSize: number
}

type VisibleTreatmentStatus = Exclude<TreatmentStatus, TreatmentStatus.Hidden>

const TREATMENT_STATUS_INDICATOR_CONFIG = {
  success: { icon: Check, backgroundColor: 'bg-emerald-500', iconSize: 8 },
  warning: { icon: EyeReview, backgroundColor: 'bg-yellow-600', iconSize: 12 },
  failure: { icon: X, backgroundColor: 'bg-neutral-400', iconSize: 8 },
} satisfies Record<string, TreatmentStatusConfig>

const TREATMENT_STATUS_CONFIG = {
  [TreatmentStatus.Approved]: TREATMENT_STATUS_INDICATOR_CONFIG.success,
  [TreatmentStatus.Covered]: TREATMENT_STATUS_INDICATOR_CONFIG.success,
  [TreatmentStatus.NeedsReview]: TREATMENT_STATUS_INDICATOR_CONFIG.warning,
  [TreatmentStatus.NotApproved]: TREATMENT_STATUS_INDICATOR_CONFIG.failure,
  [TreatmentStatus.NotCovered]: TREATMENT_STATUS_INDICATOR_CONFIG.failure,
  [TreatmentStatus.PaymentRequired]: TREATMENT_STATUS_INDICATOR_CONFIG.warning,
  [TreatmentStatus.QuoteAtTimeOfService]: TREATMENT_STATUS_INDICATOR_CONFIG.warning,
  [TreatmentStatus.Voided]: TREATMENT_STATUS_INDICATOR_CONFIG.failure,
  [TreatmentStatus.PropertyManagerReview]: TREATMENT_STATUS_INDICATOR_CONFIG.warning,
  [TreatmentStatus.Paid]: TREATMENT_STATUS_INDICATOR_CONFIG.success,
  [TreatmentStatus.Declined]: TREATMENT_STATUS_INDICATOR_CONFIG.failure,
} satisfies Record<VisibleTreatmentStatus, TreatmentStatusConfig>

type TreatmentStatusIndicatorProps = {
  status: TreatmentStatus
}

export const TreatmentStatusIndicator = ({ status }: TreatmentStatusIndicatorProps) => {
  const { t } = useTranslation(NS.TRACKING)

  if (status === TreatmentStatus.Hidden) {
    return null
  }

  const { icon: Icon, backgroundColor, iconSize } = TREATMENT_STATUS_CONFIG[status]

  return (
    <div className="flex items-center gap-1 text-xs text-muted-foreground">
      <span
        className={`flex size-3 shrink-0 items-center justify-center rounded-full ${backgroundColor}`}>
        <Icon aria-hidden="true" size={iconSize} className="text-white" />
      </span>
      <span>{t(`treatment.status.${status}`)}</span>
    </div>
  )
}
